/**
 * main.js — boot sequence and the frame update order.
 *
 * Nothing here contains gameplay logic; it exists to construct the subsystems
 * in dependency order, hand test mode its references, and drive the update
 * order every frame. If you are adding a feature, it almost certainly belongs
 * in a module rather than in this file.
 *
 * Update order matters and is deliberate:
 *   input → grab → teleport → avatar → cosmetics → stalls → hub → hud
 * Input must be sampled before grab reads button edges; grab must move held
 * bodies before the avatar moves the camera they are parented to; stalls read
 * the settled state of both.
 *
 * @module main
 */

import { GAME_VERSION, EVENTS, STALL_BY_ID } from './constants.js';
import { bus } from './core/events.js';
import { engine } from './core/engine.js';
import * as physics from './core/physics.js';
import { xr } from './core/xr.js';
import { input } from './core/input.js';
import { grab } from './core/grab.js';
import { teleport } from './core/teleport.js';
import { save, economy } from './core/save.js';
import * as testmode from './core/testmode.js';

import { gooseController } from './avatar/goose-controller.js';
import { cosmetics } from './avatar/cosmetics.js';
import { hub } from './world/hub.js';
import { hud } from './ui/hud.js';
import { perfOverlay } from './ui/perf-overlay.js';
import { vfx } from './ui/vfx.js';
import { tutorial, shouldRunTutorial } from './tutorial/index.js';

import { stallManager } from './stalls/index.js';
import * as audioModule from './audio/index.js';
import * as netModule from './net/index.js';
import * as spaceModule from './space/index.js';
import { dailyLogin } from './meta/index.js';

const boot = {
  el: document.getElementById('cc-boot'),
  status: document.getElementById('cc-boot-status'),
  set(text) {
    if (this.status) this.status.textContent = text;
  },
  hide() {
    if (!this.el) return;
    this.el.classList.add('hidden');
    setTimeout(() => this.el?.remove(), 600);
  },
};

async function start() {
  const opts = testmode.options;

  // Install the automation surface first so any error during boot is captured.
  testmode.install();
  boot.set('Loading save…');
  save.load({ fresh: opts.fresh });

  boot.set('Starting renderer…');
  engine.init();

  boot.set('Loading physics…');
  await physics.init();

  boot.set('Checking VR…');
  await xr.init(engine.renderer, engine.cameraRig);
  xr.createButton(document.getElementById('cc-xr'));

  input.init(engine.camera, engine.renderer.domElement);
  teleport.init(engine.scene, engine.cameraRig, engine.camera);
  hud.init(engine.scene, engine.camera);

  boot.set('Building the midway…');
  hub.build(engine.scene);
  gooseController.init(engine.scene, engine.cameraRig, engine.camera);

  stallManager.init({ avatar: gooseController, hub });
  spaceModule.init({
    engine, scene: engine.scene, camera: engine.camera,
    avatar: gooseController, hub,
  });
  audioModule.install();
  audioModule.audio.setListener(engine.camera);
  audioModule.ambient.start('hub');

  tutorial.init({ scene: engine.scene, camera: engine.camera, avatar: gooseController });
  vfx.install({ scene: engine.scene, camera: engine.camera });
  if (opts.perf) perfOverlay.install({ engine, physics });

  // Hand test mode live references. Without this `window.__cc` is inert.
  testmode.provide({
    engine, physics, xr, input, grab, teleport, save, economy,
    avatar: gooseController,
    stalls: stallManager,
    net: netModule,
    space: spaceModule,
    hud, hub, tutorial, perf: perfOverlay, vfx,
  });

  engine.onUpdate(update);
  engine.start();

  await engine.waitFrames(2);

  // The world is now fully built — hub, avatar, teleport nodes, the stall
  // manifest and every stub are installed, and frames are rendering. THIS is
  // the signal a self-installing module should wait for.
  //
  // It fires before the deep link (world construction precedes gameplay, so a
  // module never installs into an already-active stall) and before markReady()
  // (so `__cc.ready` cannot resolve while a listener is still installing).
  bus.emit(EVENTS.WORLD_READY, {
    engine,
    scene: engine.scene,
    hub,
    version: GAME_VERSION,
  });

  // A deep-linked stall must be live BEFORE `ready` resolves, otherwise every
  // test using ?stall= races the activation and has to guess how long to wait.
  if (opts.stall) await deepLinkToStall(opts.stall);

  testmode.markReady();
  boot.hide();

  // Kept exactly as-is for compatibility. Note this is the SECOND ENGINE_READY
  // emission — see the WORLD_READY comment above and ARCHITECTURE §3.
  bus.emit(EVENTS.ENGINE_READY, { version: GAME_VERSION });
  console.log(`[carnival-chaos] v${GAME_VERSION} ready — ${opts.test ? 'TEST MODE' : 'normal'}`);

  // Onboarding and the daily reward both want the player's attention the
  // moment they arrive, so they take turns: a first-timer gets the tutorial,
  // everyone else gets the reward toast.
  if (shouldRunTutorial()) tutorial.start();
  else if (!opts.skipTutorial) checkDailyLogin();
}

/**
 * One frame of game logic. Physics has already stepped and meshes are synced
 * by the time this runs.
 * @param {number} dt Seconds since the last frame, clamped.
 */
function update(dt) {
  input.update(dt);
  grab.update(dt);
  teleport.update(dt);
  gooseController.update(dt);
  cosmetics.update(dt);
  stallManager.update(dt);
  spaceModule.update(dt);
  hub.update(dt);
  hud.update(dt);
  tutorial.update(dt);
  vfx.update(dt);
  perfOverlay.update(dt);

  if (input.wasPressed('activate')) stallManager.activateNearby();
  if (input.wasPressed('exit') && stallManager.activeId()) stallManager.exit();
}

/** `?stall=<id>` — walk up to a stall and start a round. */
async function deepLinkToStall(id) {
  if (!STALL_BY_ID[id]) {
    console.warn(`[main] ?stall=${id} is not a known stall id`);
    return;
  }
  // Placed directly rather than via teleport.to() — a blink would land its
  // delayed move after activation and fight the stall's first-person anchor.
  const plot = hub.getPlot(id);
  if (plot) gooseController.setPosition(plot.playPosition, plot.yaw);
  await engine.waitFrames(2);
  await stallManager.activate(id);
}

/** GDD 8.7 — surface the daily reward if one is due. */
function checkDailyLogin() {
  const reward = dailyLogin.check();
  if (!reward) return;
  hud.toast(`Day ${reward.day}: +${reward.tickets} tickets`, 'prize', 3600);
}

start().catch((err) => {
  console.error('[carnival-chaos] boot failed:', err);
  if (Array.isArray(window.__ccErrors)) window.__ccErrors.push(`boot: ${err.message}`);
  boot.set(`Boot failed: ${err.message}`);
});
